// 사유코드 관리 «WMS 상세사유 분류» 카드 — 1.0 / 2.0 탭.
// · 1.0 = 게이트웨이 정본 코드표(GET reasons 의 v1_codes · WMS 에 없어 동기화 없음): 코드 · 이름(편집) · 분류 · 사용 · 연결된 사유 · [저장]
//   + [1.0 코드 추가](코드 · 이름 · 분류 → createV1Code).
// · 2.0 = 캐시(v2_codes 의 CLAIM_RESN_DETAIL_CD) ∪ 분류가 저장된 코드(v2_categories): 코드 · 이름 · 분류 · 연결된 사유 · [저장].
//   분류가 없는 코드는 review(판단 필요)로 보인다.
// 🔴 canEdit 가 거짓이면 입력은 disabled 이고 저장·추가 단추는 그리지 않는다. 삭제 단추는 없다(사용 중지만).
// 🔴 저장은 바뀐 칸만(1.0) · 분류가 그대로면 요청하지 않는다(2.0) · 저장은 부모의 runSave(성공하면 GET 을 다시 읽는다)
//    · 실패는 detail.message 를 그 줄(role=alert)과 toast(runSave) 로. version 이 바뀌면 줄 입력을 새 값으로 다시 채운다.
import { useMemo, useState } from 'react';
import { Button, PillTabs } from '../../components/ui';
import { claimsAPI } from '../../api/client';
import { COLORS, card, input } from '../../utils/uiStyles';
import { FALLBACK_LABELS, V2_DETAIL_GROUP, changedFields, textOrNull } from './claimLabels';

const ID_BASE = 'claim-wms-reason';
const TABS = [{ key: 'v1', label: '1.0' }, { key: 'v2', label: '2.0' }];
const DEFAULT_CATEGORY = 'review';
const NO_CHANGE = '바뀐 칸이 없습니다.';
const DASH = '–';
const V1_COLUMNS = ['코드', '이름', '분류', '사용', '연결된 사유'];
const V2_COLUMNS = ['코드', '이름', '분류', '연결된 사유'];
const th = { textAlign: 'left', padding: '7px 8px', fontSize: '12px', fontWeight: 500, color: COLORS.textSub, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const errText = { color: COLORS.dangerText, fontSize: '12px', marginTop: '4px' };
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub };

const withReason = (body, reason) => {
  const r = textOrNull(reason);
  return r ? { ...body, reason: r } : body;
};
// 사유코드 → 연결 코드 목록에서 «코드 → 연결된 사유코드들» 표를 만든다.
function linkedBy(items, listKey, codeKey) {
  const map = new Map();
  items.forEach((it) => (Array.isArray(it[listKey]) ? it[listKey] : []).forEach((link) => {
    const code = link[codeKey];
    if (!map.has(code)) map.set(code, []);
    map.get(code).push(it.code);
  }));
  return map;
}
const linkedText = (codes) => (codes && codes.length ? codes.join(', ') : DASH);

function CategorySelect({ label, value, options, disabled, onChange }) {
  return (
    <select aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ ...input, width: '150px' }}>
      {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
    </select>
  );
}

function V1Row({ row, canEdit, categories, linked, runSave, reason }) {
  const [name, setName] = useState(row.name || '');
  const [category, setCategory] = useState(row.category || DEFAULT_CATEGORY);
  const [active, setActive] = useState(!!row.active);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const label = `1.0 ${row.code}`;

  const onSave = async () => {
    const body = changedFields(
      { name: row.name, category: row.category, active: !!row.active },
      { name: name.trim(), category, active },
    );
    if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
    setBusy(true);
    setError('');
    const res = await runSave(() => claimsAPI.patchV1Code(row.code, withReason(body, reason)), '1.0 상세사유를 저장했습니다');
    setBusy(false);
    if (res && res.error) setError(res.error);
  };

  return (
    <tr data-testid="claim-v1-code-row" data-code={row.code}>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.code}</td>
      <td style={td}>
        <input type="text" aria-label={`${label} 이름`} value={name} maxLength={100} disabled={!canEdit}
          onChange={(e) => setName(e.target.value)} style={{ ...input, width: '180px' }} />
      </td>
      <td style={td}>
        <CategorySelect label={`${label} 분류`} value={category} options={categories} disabled={!canEdit} onChange={setCategory} />
      </td>
      <td style={td}>
        <input type="checkbox" aria-label={`${label} 사용`} checked={active} disabled={!canEdit} onChange={(e) => setActive(e.target.checked)} />
      </td>
      <td style={td}>{linkedText(linked)}</td>
      {canEdit && (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          <Button variant="secondary" size="sm" aria-label={`${label} 저장`} onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>
          {error && <div role="alert" style={errText}>{error}</div>}
        </td>
      )}
    </tr>
  );
}

function V2Row({ row, canEdit, categories, linked, runSave, reason }) {
  const [category, setCategory] = useState(row.category);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const label = `2.0 ${row.code}`;

  const onSave = async () => {
    if (category === row.category) { setError(NO_CHANGE); return; }
    setBusy(true);
    setError('');
    const res = await runSave(() => claimsAPI.putV2Category(row.code, withReason({ category }, reason)), '2.0 분류를 저장했습니다');
    setBusy(false);
    if (res && res.error) setError(res.error);
  };

  return (
    <tr data-testid="claim-v2-category-row" data-code={row.code}>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.code}</td>
      <td style={td}>
        {row.name || DASH}
        {!row.inCache && <span style={{ color: COLORS.dangerText, marginLeft: '6px' }}>코드표 없음</span>}
      </td>
      <td style={td}>
        <CategorySelect label={`${label} 분류`} value={category} options={categories} disabled={!canEdit} onChange={setCategory} />
      </td>
      <td style={td}>{linkedText(linked)}</td>
      {canEdit && (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          <Button variant="secondary" size="sm" aria-label={`${label} 저장`} onClick={onSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</Button>
          {error && <div role="alert" style={errText}>{error}</div>}
        </td>
      )}
    </tr>
  );
}

function V1AddForm({ categories, runSave, reason, onDone }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // 빈 이름·같은 코드도 서버에 보내 그 문구(422·409)를 그대로 보인다.
  const onAdd = async () => {
    setBusy(true);
    setError('');
    const body = withReason({ code: code.trim(), name: name.trim(), category }, reason);
    const res = await runSave(() => claimsAPI.createV1Code(body), '1.0 상세사유를 추가했습니다');
    setBusy(false);
    if (res && res.error) setError(res.error);
    else onDone();
  };

  return (
    <div data-testid="claim-v1-code-new" style={{ border: `1px solid ${COLORS.borderSoft}`, borderRadius: '6px', padding: '10px 12px', margin: '10px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' }}>
        <label style={fieldBox}>
          <span>새 1.0 코드</span>
          <input type="text" value={code} maxLength={20} placeholder="영문·숫자 1~20자" onChange={(e) => setCode(e.target.value)} style={{ ...input, width: '120px' }} />
        </label>
        <label style={fieldBox}>
          <span>새 1.0 이름</span>
          <input type="text" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} style={{ ...input, width: '200px' }} />
        </label>
        <div style={fieldBox}>
          <span aria-hidden="true">분류</span>
          <CategorySelect label="새 1.0 분류" value={category} options={categories} onChange={setCategory} />
        </div>
        <Button onClick={onAdd} disabled={busy}>{busy ? '추가 중…' : '추가'}</Button>
        <Button variant="secondary" onClick={onDone} disabled={busy}>취소</Button>
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
    </div>
  );
}

export default function ClaimWmsReasonPanel({ data, canEdit, runSave, version = 0 }) {
  const [tab, setTab] = useState('v1');
  const [adding, setAdding] = useState(false);
  const [reason, setReason] = useState('');

  const items = useMemo(() => (data && Array.isArray(data.items) ? data.items : []), [data]);
  const categories = useMemo(() => {
    const list = data && Array.isArray(data.categories) ? data.categories : [];
    return list.length ? list : Object.entries(FALLBACK_LABELS.category).map(([code, label]) => ({ code, label }));
  }, [data]);
  const v1Rows = useMemo(() => (data && Array.isArray(data.v1_codes) ? data.v1_codes : []), [data]);
  const v2Rows = useMemo(() => {
    const saved = (data && data.v2_categories && typeof data.v2_categories === 'object') ? data.v2_categories : {};
    const names = new Map(
      (data && Array.isArray(data.v2_codes) ? data.v2_codes : [])
        .filter((c) => c.code_group === V2_DETAIL_GROUP)
        .map((c) => [c.code, c.name]),
    );
    const codes = Array.from(new Set([...names.keys(), ...Object.keys(saved)])).sort();
    return codes.map((code) => ({
      code, name: names.get(code) || null, inCache: names.has(code), category: saved[code] || DEFAULT_CATEGORY,
    }));
  }, [data]);
  const v1Linked = useMemo(() => linkedBy(items, 'v1_links', 'v1_code'), [items]);
  const v2Linked = useMemo(() => linkedBy(items, 'v2_links', 'v2_detail_cd'), [items]);

  const v1 = tab === 'v1';
  const columns = [...(v1 ? V1_COLUMNS : V2_COLUMNS), ...(canEdit ? [''] : [])];

  return (
    <section data-testid="claim-wms-reason-panel" aria-labelledby={`${ID_BASE}-title`} style={{ ...card, marginTop: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <h2 id={`${ID_BASE}-title`} style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>WMS 상세사유 분류</h2>
        <span style={{ fontSize: '12px', color: COLORS.textMuted }}>사고 후보 · 화주·고객 사유 · 판단 필요 — 클레임 불러오기에서 상세사유를 나누는 기준</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <PillTabs idBase={ID_BASE} ariaLabel="WMS 상세사유" value={tab} onChange={(key) => { setTab(key); setAdding(false); }}
          items={TABS.map((t) => ({ ...t, count: t.key === 'v1' ? v1Rows.length : v2Rows.length }))} />
        {canEdit && (
          <label style={{ ...fieldBox, flexDirection: 'row', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
            <span>변경 사유(선택)</span>
            <input type="text" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} style={{ ...input, width: '200px' }} />
          </label>
        )}
        {canEdit && v1 && !adding && (
          <Button onClick={() => setAdding(true)}><span aria-hidden="true">+ </span>1.0 코드 추가</Button>
        )}
      </div>
      {v1 && (
        <p style={{ margin: '0 0 8px', fontSize: '12px', color: COLORS.textMuted }}>
          1.0 상세사유 코드표는 WMS 에 없어 여기 값이 기준입니다(동기화 없음). 쓰지 않는 코드는 사용을 끕니다.
        </p>
      )}
      {canEdit && v1 && adding && (
        <V1AddForm categories={categories} runSave={runSave} reason={reason} onDone={() => setAdding(false)} />
      )}
      <div role="tabpanel" id={`${ID_BASE}-panel-${tab}`} aria-labelledby={`${ID_BASE}-tab-${tab}`} style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{columns.map((h, i) => <th key={`${h}-${i}`} scope="col" style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {v1 && v1Rows.length === 0 && (
              <tr><td colSpan={columns.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>1.0 상세사유 코드가 없습니다.</td></tr>
            )}
            {!v1 && v2Rows.length === 0 && (
              <tr><td colSpan={columns.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>2.0 상세사유 코드가 없습니다. 2.0 사유코드를 동기화하세요.</td></tr>
            )}
            {v1 && v1Rows.map((row) => (
              <V1Row key={`${row.code}-${version}`} row={row} canEdit={canEdit} categories={categories}
                linked={v1Linked.get(row.code)} runSave={runSave} reason={reason} />
            ))}
            {!v1 && v2Rows.map((row) => (
              <V2Row key={`${row.code}-${version}`} row={row} canEdit={canEdit} categories={categories}
                linked={v2Linked.get(row.code)} runSave={runSave} reason={reason} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
